import * as tslib_1 from "tslib";
import { pick } from '@nextiva/analytics-common';
import { Checkbox, InfiniteScroll, SearchField, SpinnerSvg } from '@nextiva/next-ui';
import cx from 'classnames';
import { assign, debounce, difference, filter, has, isEmpty, isEqual, map, some, xorBy } from 'lodash';
import React from 'react';
import NotFound from '../NotFound';
import Styles from "./MultiSelectable.css";

var defaultMultiOptionComponent = function defaultMultiOptionComponent(_a) {
  var option = _a.option,
      handlerChangeOption = _a.handlerChangeOption,
      isSelected = _a.isSelected;
  return React.createElement("div", {
    key: option.id,
    className: Styles.item,
    onClick: handlerChangeOption,
    "data-state-icon": isSelected ? '-' : '+',
    "data-test": "multi-option"
  }, React.createElement(Checkbox, {
    checked: isSelected,
    onChange: handlerChangeOption
  }), option.name);
};

var INIT_PAGE = {
  hasMore: true,
  page: -1,
  filteredOptions: []
};

var MultiSelectable =
/** @class */
function (_super) {
  tslib_1.__extends(MultiSelectable, _super);

  function MultiSelectable(props) {
    var _a, _b;

    var _this = _super.call(this, props) || this;

    _this.changePage = function (newState, nextProps) {
      var attrs = ['hasMore', 'page'];

      if (!isEqual(pick(nextProps, attrs), pick(_this.state, attrs))) {
        assign(newState, pick(nextProps, attrs));
      }
    };

    _this.changeCategory = function (newState, nextProps) {
      var activeCategoryId = nextProps.activeCategoryId,
          hasMore = nextProps.hasMore,
          options = nextProps.options,
          async = nextProps.async;
      var shouldReload = _this.state.shouldReload;

      if (async && _this.props.activeCategoryId !== activeCategoryId) {
        _this.addCategoryToReloadList(newState, nextProps);

        if (!options.length && !hasMore || shouldReload[activeCategoryId]) {
          // clear page and hasMore for InifinityScroll
          // which fires loadOptions by hasMore changes;
          // for reloading option if before got empty;
          // or other related options was changed
          assign(newState, INIT_PAGE);
        }
      }
    };

    _this.addCategoryToReloadList = function (newState, nextProps) {
      var _a;

      var activeCategoryId = nextProps.activeCategoryId;
      var shouldReload = _this.state.shouldReload;

      if (!has(shouldReload, "" + activeCategoryId)) {
        assign(newState, {
          shouldReload: assign({}, shouldReload, (_a = {}, _a[activeCategoryId] = false, _a))
        });
      }
    };

    _this.filterOptions = function (searchValue) {
      return function () {
        var _a = _this.props,
            options = _a.options,
            async = _a.async;

        if (async) {
          // clear page and hasMore for InifinityScroll,
          // wich fire loadOptions by hasMore change
          _this.setState(INIT_PAGE);
        } else {
          var filteredOptions = filter(options, function (option) {
            return option.name.toLowerCase().includes(searchValue.toLowerCase());
          });

          _this.setState({
            filteredOptions: filteredOptions
          });
        }
      };
    };

    _this.handlerSearch = function (event, value) {
      var activeCategoryId = _this.props.activeCategoryId;

      var searchValues = tslib_1.__assign({}, _this.state.searchValues);

      searchValues[activeCategoryId] = value;

      _this.setState({
        searchValues: searchValues
      }, function () {
        if (_this.debouncedFiltered) {
          _this.debouncedFiltered.cancel();
        }

        _this.debouncedFiltered = debounce(_this.filterOptions(value), 400);

        _this.debouncedFiltered();
      });
    };

    _this.updateSelected = function (nextSelectedOptions) {
      _this.updateDirtyForCategory(nextSelectedOptions);

      _this.props.handlerUpdateSelected(nextSelectedOptions);
    };

    _this.updateDirtyForCategory = function (nextSelectedOptions) {
      var _a;

      var _b = _this.props,
          activeCategoryId = _b.activeCategoryId,
          isDirty = _b.isDirty;
      var _c = _this.state,
          shouldReload = _c.shouldReload,
          prevSelectedOptions = _c.prevSelectedOptions;
      var isChanged = Boolean(difference(nextSelectedOptions[activeCategoryId], prevSelectedOptions[activeCategoryId]).length);

      var nextDirty = tslib_1.__assign({}, isDirty, (_a = {}, _a[activeCategoryId] = isChanged, _a));

      _this.props.handlerUpdateDirtyCategory(nextDirty);

      if (isChanged) {
        var newState_1 = {
          shouldReload: shouldReload
        };
        map(shouldReload, function (val, categoryId) {
          if (activeCategoryId !== categoryId) {
            newState_1.shouldReload[categoryId] = true;
          }
        });

        _this.setState(newState_1);
      }
    };

    _this.toggleVisibleOptions = function () {
      var activeCategoryId = _this.props.activeCategoryId;

      var selectedOptions = tslib_1.__assign({}, _this.props.selectedOptions);

      selectedOptions[activeCategoryId] = _this.isSomeSelected() ? [] : _this.state.filteredOptions;

      _this.updateSelected(selectedOptions);
    };

    _this.handlerChangeOption = function (option) {
      return function () {
        var activeCategoryId = _this.props.activeCategoryId;

        var selectedOptions = tslib_1.__assign({}, _this.props.selectedOptions);

        selectedOptions[activeCategoryId] = xorBy(selectedOptions[activeCategoryId], [option], 'id');

        _this.updateSelected(selectedOptions);
      };
    };

    _this.isSomeSelected = function () {
      var _a = _this.props,
          activeCategoryId = _a.activeCategoryId,
          selectedOptions = _a.selectedOptions;
      return selectedOptions && selectedOptions[activeCategoryId] && !!selectedOptions[activeCategoryId].length;
    };

    _this.isSelected = function (option) {
      var _a = _this.props,
          activeCategoryId = _a.activeCategoryId,
          selectedOptions = _a.selectedOptions;
      return selectedOptions && selectedOptions[activeCategoryId] && some(selectedOptions[activeCategoryId], {
        id: option.id
      });
    };

    _this.loadOptions = function () {
      var _a;

      var _b = _this.props,
          activeCategoryId = _b.activeCategoryId,
          loadOptions = _b.loadOptions;
      var _c = _this.state,
          searchValues = _c.searchValues,
          page = _c.page,
          shouldReload = _c.shouldReload;

      if (shouldReload[activeCategoryId]) {
        _this.setState({
          shouldReload: assign({}, shouldReload, (_a = {}, _a[activeCategoryId] = false, _a))
        });
      }

      if (loadOptions) {
        loadOptions(page, activeCategoryId, searchValues[activeCategoryId]);
      }
    };

    _this.renderOptions = function () {
      var _a = _this.state,
          options = _a.filteredOptions,
          hasMore = _a.hasMore;
      var multiOptionComponent = _this.props.multiOptionComponent;

      if (options.length) {
        return map(options, function (option) {
          return multiOptionComponent({
            option: option,
            handlerChangeOption: _this.handlerChangeOption(option),
            isSelected: _this.isSelected(option)
          });
        });
      } else if (!hasMore) {
        return React.createElement(NotFound, null);
      }
    };

    var activeCategoryId = props.activeCategoryId,
        selectedOptions = props.selectedOptions,
        search = props.search,
        options = props.options,
        hasMore = props.hasMore,
        page = props.page;
    _this.state = {
      searchValues: (_a = {}, _a[activeCategoryId] = search, _a),
      filteredOptions: options,
      page: page,
      hasMore: hasMore,
      prevSelectedOptions: selectedOptions,
      shouldReload: (_b = {}, _b[activeCategoryId] = false, _b)
    };
    return _this;
  }

  MultiSelectable.prototype.componentWillReceiveProps = function (nextProps) {
    var newState = {
      filteredOptions: nextProps.options
    };
    this.changePage(newState, nextProps);
    this.changeCategory(newState, nextProps);

    if (!isEmpty(newState)) {
      this.setState(newState);
    }
  };

  MultiSelectable.prototype.render = function () {
    var _a, _b;

    var _c = this.props,
        async = _c.async,
        title = _c.title,
        activeCategoryId = _c.activeCategoryId,
        error = _c.error;
    var _d = this.state,
        hasMore = _d.hasMore,
        filteredOptions = _d.filteredOptions;
    var searchValue = this.state.searchValues[activeCategoryId];
    var isSomeSelected = this.isSomeSelected();
    return React.createElement("div", {
      className: cx(Styles.multiSelectable, (_a = {}, _a[Styles.error] = error, _a)),
      "data-test": "multi-selectable"
    }, React.createElement("div", {
      className: Styles.header
    }, React.createElement("h3", {
      className: Styles.title
    }, title), React.createElement("div", {
      className: Styles.headerControls
    }, React.createElement("div", {
      className: Styles.headerControlsItem,
      onClick: this.toggleVisibleOptions,
      "data-test": "multi-selectable-control-options"
    }, isSomeSelected ? 'Remove All' : 'Select All'))), React.createElement("div", {
      className: Styles.search
    }, React.createElement(SearchField, {
      value: searchValue,
      onChange: this.handlerSearch
    })), error && React.createElement("div", {
      className: Styles.errorMessage,
      "data-test": "multi-selectable-error"
    }, "You need to add some options"), async ? React.createElement(InfiniteScroll, {
      className: cx(Styles.options, (_b = {}, _b[Styles.emptyOptions] = filteredOptions.length === 0, _b)),
      loadMore: this.loadOptions,
      hasMore: hasMore,
      threshold: 50,
      spinner: React.createElement(SpinnerSvg, {
        size: 30,
        thickness: 2,
        style: {
          height: '100%'
        },
        className: Styles.spinner
      })
    }, this.renderOptions()) : React.createElement("div", {
      className: Styles.options
    }, this.renderOptions()), this.props.children);
  };

  MultiSelectable.defaultProps = {
    async: false,
    title: 'Multiselect',
    search: '',
    hasMore: INIT_PAGE.hasMore,
    page: INIT_PAGE.page,
    multiOptionComponent: defaultMultiOptionComponent
  };
  return MultiSelectable;
}(React.PureComponent);

export default MultiSelectable;